import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DollarSign, FileText, BookOpen, Zap, Clock } from 'lucide-react';
import { WorkshopForm } from './WorkshopForm';
import { motion } from 'framer-motion';
import { learnEarnAPI } from '../../services/api';

// Mock data - in production, this would come from backend/database
const mockWorkItems = [
  {
    id: 1,
    type: 'assignment',
    title: 'Mathematics Assignment',
    description: 'Assignment of 10 pages with diagrams',
    pages: 10,
    hasDiagrams: true,
    hasQuestionsPDF: true,
    amount: 300,
    deadline: '2026-01-25',
  },
  {
    id: 2,
    type: 'journal',
    title: 'Science Journal Writing',
    description: 'Complete science journal - 15 pages',
    pages: 15,
    amount: 450,
    deadline: '2026-01-28',
  },
  {
    id: 3,
    type: 'assignment',
    title: 'English Literature Assignment',
    description: 'Essay writing assignment - 8 pages',
    pages: 8,
    hasDiagrams: false,
    hasQuestionsPDF: true,
    amount: 250,
    deadline: '2026-01-30',
  },
  {
    id: 4,
    type: 'journal',
    title: 'History Journal',
    description: 'History journal writing with references',
    pages: 20,
    amount: 600,
    deadline: '2026-02-05',
  },
];

export function WorkshopList() {
  const [selectedWork, setSelectedWork] = useState(null);
  const [activePortal, setActivePortal] = useState('assignments');
  const [workItems, setWorkItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch work items from API
  useEffect(() => {
    const fetchWorkItems = async () => {
      try {
        setLoading(true);

        // Load from localStorage first for immediate display
        const localWorkshopItems = JSON.parse(localStorage.getItem('workshopItems') || '[]');

        // Fetch both workshop work items and customer work submissions
        const [workshopResponse, customerWorkResponse] = await Promise.all([
          learnEarnAPI.workshop.getWorkItems(1, 20),
          learnEarnAPI.customerWork.getAll(1, 50, '', '') // Get all customer work submissions
        ]);

        // Transform customer work submissions to work item format
        const transformCustomerWorkToWorkItem = (customerWork) => ({
          _id: customerWork._id,
          id: customerWork._id,
          title: `${customerWork.workType === 'assignment' ? 'Assignment' : 'Journal'} - ${customerWork.name}`,
          description: customerWork.additionalInfo || customerWork.description || `${customerWork.workType} work requested`,
          type: customerWork.workType,
          pages: customerWork.pages || 0,
          amount: customerWork.workshopAmount || customerWork.calculatedAmount || customerWork.amount || 0,
          originalAmount: customerWork.originalAmount || customerWork.amount || 0,
          deadline: customerWork.deadline,
          // Additional customer info
          customerName: customerWork.name,
          customerEmail: customerWork.email,
          customerPhone: customerWork.phone,
          hasDiagrams: customerWork.workType === 'assignment',
          hasQuestionsPDF: customerWork.workType === 'assignment',
          // Mark as customer work for identification
          isCustomerWork: true,
          isOTSWork: true
        });

        // Get workshop items
        const workshopItems = workshopResponse.data || [];

        // Get and transform customer work submissions
        const customerWorkItems = (customerWorkResponse.data || [])
          .map(transformCustomerWorkToWorkItem);

        // Merge all sources: localStorage + API data
        const allWorkItems = [...localWorkshopItems, ...workshopItems, ...customerWorkItems];
        
        // Remove duplicates based on id
        const uniqueWorkItems = allWorkItems.filter((item, index, self) =>
          index === self.findIndex((t) => t.id === item.id || t._id === item._id)
        );

        setWorkItems(uniqueWorkItems);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch work items:', err);
        console.log('Using mock data as fallback');
        
        // Load from localStorage even if API fails
        const localWorkshopItems = JSON.parse(localStorage.getItem('workshopItems') || '[]');
        
        // Fallback to localStorage only — no mock data (mock items have no valid MongoDB _id)
        setWorkItems([...localWorkshopItems]);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchWorkItems();
  }, []);

  if (selectedWork) {
    return (
      <WorkshopForm
        workItem={selectedWork}
        onBack={() => setSelectedWork(null)}
      />
    );
  }

  // Separate assignments and journals from API data
  const assignments = workItems.filter(item => item.type === 'assignment');
  const journals = workItems.filter(item => item.type === 'journal');

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-foreground font-medium">Loading work opportunities...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="text-center py-16 bg-destructive/10 rounded-2xl border border-destructive/30">
        <div className="w-16 h-16 bg-destructive/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-destructive" />
        </div>
        <p className="text-destructive font-medium text-lg mb-2">Oops! Something went wrong</p>
        <p className="text-destructive/80 text-sm">{error}</p>
        <Button
          onClick={() => window.location.reload()}
          className="mt-4 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
        >
          Try Again
        </Button>
      </div>
    );
  }

  const renderWorkItems = (items, startIndex = 0) => (
    items.map((item, index) => (
      <motion.div
        key={item.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: (startIndex + index) * 0.1 }}
        className="bg-card rounded-xl border border-border hover:shadow-lg transition-all duration-300 cursor-pointer hover:border-accent/50"
        onClick={() => setSelectedWork(item)}
      >
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${item.type === 'assignment'
                  ? 'bg-blue-500/10 text-blue-500'
                  : 'bg-emerald-500/10 text-emerald-500'
                }`}>
                {item.type === 'assignment' ? (
                  <FileText className="w-6 h-6" />
                ) : (
                  <BookOpen className="w-6 h-6" />
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Badge
                className={`${item.type === 'assignment'
                  ? 'bg-blue-500/10 text-blue-500 border-blue-500/20'
                  : 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'} font-medium`}
              >
                {item.type === 'assignment' ? '📝 Assignment' : '📔 Journal'}
              </Badge>
              {item.isOTSWork && (
                <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20 font-medium text-xs">
                  🔥 OTS Work
                </Badge>
              )}
            </div>
          </div>

          <div className="flex items-center gap-6 mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Pages</p>
                <p className="font-semibold text-gray-900">{item.pages}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Payment</p>
                <p className="font-semibold text-green-700">₹{item.amount}</p>
                {item.originalAmount && item.originalAmount !== item.amount && (
                  <p className="text-xs text-gray-400 line-through">₹{item.originalAmount}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-4 h-4 text-orange-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Deadline</p>
                <p className="font-semibold text-orange-700 text-sm">{new Date(item.deadline).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</p>
              </div>
            </div>
          </div>

          {item.type === 'assignment' && (
            <div className="flex gap-2 mb-4">
              {item.hasDiagrams && (
                <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50">
                  ✏️ With Diagrams
                </Badge>
              )}
              {item.hasQuestionsPDF && (
                <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50">
                  📄 Questions PDF
                </Badge>
              )}
            </div>
          )}

          {item.isOTSWork && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-xs text-orange-700">
                <span className="font-semibold">💰 Platform Fee Applied:</span> This work shows the amount after 10% platform fee deduction
              </p>
            </div>
          )}

          <Button
            className={`w-full py-3 text-sm font-semibold transition-all ${item.type === 'assignment' 
              ? 'bg-blue-500 hover:bg-blue-600 text-white' 
              : 'bg-emerald-500 hover:bg-emerald-600 text-white'} border-0`}
            onClick={() => setSelectedWork(item)}
          >
            Apply for this Work →
          </Button>
        </div>
      </motion.div>
    ))
  );

  return (
    <div className="space-y-6">
      <motion.div
        className="bg-gradient-to-r from-cyan-500 to-blue-600 p-5 rounded-2xl text-white shadow-lg"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
            <Zap className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-semibold mb-1">Available Work Opportunities</h4>
            <p className="text-sm text-cyan-50">
              Browse through the list below and select work that matches your skills. Complete the work on time to earn money!
            </p>
          </div>
        </div>
      </motion.div>

      {/* Portal Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-lg p-2"
      >
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={() => setActivePortal('assignments')}
            className={`flex items-center justify-center gap-3 p-4 rounded-xl transition-all duration-300 ${activePortal === 'assignments'
                ? 'bg-white text-blue-600 shadow-lg'
                : 'bg-white/10 text-white hover:bg-white/20'
              }`}
          >
            <FileText className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold text-sm">📝 Assignments</p>
              <p className="text-xs opacity-80">{assignments.length} Available</p>
            </div>
          </button>

          <button
            onClick={() => setActivePortal('journals')}
            className={`flex items-center justify-center gap-3 p-4 rounded-xl transition-all duration-300 ${activePortal === 'journals'
                ? 'bg-white text-emerald-600 shadow-lg'
                : 'bg-white/10 text-white hover:bg-white/20'
              }`}
          >
            <BookOpen className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold text-sm">📔 Journals</p>
              <p className="text-xs opacity-80">{journals.length} Available</p>
            </div>
          </button>
        </div>
      </motion.div>

      {/* Content based on active portal */}
      {activePortal === 'assignments' && assignments.length > 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-2xl border-2 border-blue-200 shadow-lg">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-blue-500 to-cyan-500 p-5 rounded-xl text-white mb-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">📝 Assignment Work</h2>
                  <p className="text-cyan-50 text-sm">Complete assignments with diagrams and detailed solutions</p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white border-white/30 px-3 py-1 text-sm backdrop-blur-sm">
                {assignments.length} Available
              </Badge>
            </div>
          </motion.div>
          <div className="grid gap-5">
            {renderWorkItems(assignments, 0)}
          </div>
        </div>
      )}

      {activePortal === 'journals' && journals.length > 0 && (
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border-2 border-emerald-200 shadow-lg">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 p-5 rounded-xl text-white mb-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <BookOpen className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">📔 Journal Work</h2>
                  <p className="text-emerald-50 text-sm">Write comprehensive journals with proper formatting</p>
                </div>
              </div>
              <Badge className="bg-white/20 text-white border-white/30 px-3 py-1 text-sm backdrop-blur-sm">
                {journals.length} Available
              </Badge>
            </div>
          </motion.div>
          <div className="grid gap-5">
            {renderWorkItems(journals, 0)}
          </div>
        </div>
      )}

      {/* Empty States */}
      {activePortal === 'assignments' && assignments.length === 0 && (
        <div className="text-center py-16 bg-muted/30 rounded-2xl border border-border">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-10 h-10 text-muted-foreground" />
          </div>
          <p className="text-foreground font-medium text-lg">No assignments available right now</p>
          <p className="text-muted-foreground text-sm">Check back later for new assignment opportunities!</p>
        </div>
      )}

      {activePortal === 'journals' && journals.length === 0 && (
        <div className="text-center py-16 bg-accent/5 rounded-2xl border border-accent/20">
          <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-10 h-10 text-accent" />
          </div>
          <p className="text-foreground font-medium text-lg">No journal work available right now</p>
          <p className="text-muted-foreground text-sm">Check back later for new journal opportunities!</p>
        </div>
      )}
    </div>
  );
}